import { useState } from 'react';

import { useGetUser } from '../../hooks/queries/user';

import AddressActions from '../../flux/actions/AddressActions';

import ValueConstants from '../../constants/ValueConstants';

import LocationRestrictionErrorMessage from '../LocationRestrictionErrorMessage';

import { PropertyConstants } from '../../constants/PropertyConstants';

import type { Dispatch, SetStateAction } from 'react';
import type { PropertyTypesValues } from '../../constants/PropertyConstants';
import type AddressDetails from '../../models/AddressDetails';
import type { LocationRestrictionProps } from '../LocationRestrictionErrorMessage';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO },
  LISTING_TYPES: { RENTAL, ROOM },
  LISTING_CODES_BY_TYPE,
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES_BY_CODE,
} = PropertyConstants;

export const useLocationRestriction = ({
  isLoggedIn,
  updateDuplicateAccountListingId,
  updateErrorObj,
  updateIsLoading,
  setAddressDetails,
}: {
  isLoggedIn: boolean;
  updateDuplicateAccountListingId: (arg: string) => void;
  updateErrorObj: (arg: {
    address?: string | JSX.Element;
    propertyType?: string | JSX.Element;
    numberOfUnits?: string;
  }) => void;
  updateIsLoading: (arg: boolean) => void;
  setAddressDetails: (arg: ReturnType<typeof AddressDetails>) => void;
}) => {
  const { data: user } = useGetUser();

  const [propertyTypeFromApi, updatePropertyTypeFromApi] = useState('');

  async function checkLocationRestriction({
    address,
    unit,
    isRoomForRent,
    propertyType,
    hasAuthCookies,
    numUnitsError = '',
  }: {
    address: string;
    unit: string;
    isRoomForRent: boolean;
    propertyType: string;
    hasAuthCookies?: string;
    numUnitsError?: string;
  }) {
    updateIsLoading(true);

    if (!address) {
      updateIsLoading(false);

      return false;
    }

    const getRestrictionsResponse = await (
      isLoggedIn || hasAuthCookies
        ? AddressActions.getRestrictions({
            address,
            unit,
            listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
            propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType as PropertyTypesValues] : null,
          })
        : AddressActions.getPublicRestrictions({
            address,
            unit,
            listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
            propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType as PropertyTypesValues] : null,
          })
    )().catch(() => {
      // call failed, do not update errorObj
      updateIsLoading(false);
    });

    const newErrorObj: {
      address: string | JSX.Element;
      propertyType?: string | JSX.Element;
      numberOfUnits: string;
    } = {
      address: '',
      propertyType: '',
      numberOfUnits: numUnitsError,
    };

    if (getRestrictionsResponse) {
      const errorMessage = LocationRestrictionErrorMessage(
        getRestrictionsResponse as LocationRestrictionProps,
        propertyType === COMMUNITY,
        user.isMultifamilyCustomer,
        false,
        user.isSingleFamilyEnterpriseCustomer,
        user.isPostingPathAdmin,
        user.isHousingConnector,
      );

      if (errorMessage) {
        // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by
        // getLocationRestrictionErrorMessage(), then auto set the property to be community, unless
        // the user manually input a unit, which would make it a condo.
        if (getRestrictionsResponse.paidMultifamilyLocation) {
          if (!propertyType) {
            const propertyTypeToShow = unit ? CONDO : COMMUNITY;
            updatePropertyTypeFromApi(propertyTypeToShow);
          }

          newErrorObj.propertyType = errorMessage;
          updateErrorObj(newErrorObj);
          updateIsLoading(false);
          return false;
        }

        newErrorObj.address = errorMessage;
      }

      // only for logged in users
      if (getRestrictionsResponse.duplicateAccountListingId) {
        updateDuplicateAccountListingId(
          (getRestrictionsResponse as LocationRestrictionProps).duplicateAccountListingId,
        );
      }
    }

    const getDetailsRes = await AddressActions.getDetails(
      { address, unit: propertyType === CONDO ? unit : '' },
      setAddressDetails,
    )().catch((err) => {
      if (err) {
        if (typeof err === 'string') {
          // Unable to match address
          // appLogger.logError({
          //   message: err,
          //   context: 'PostingSearchModule#useLocationRestriction',
          // }); // logger is not supported on ZMC yet

          // eslint-disable-next-line
          console.error(`ERROR: ${err} :: Context: PostingSearchModule#useLocationRestriction`);
        }

        if (err.message) {
          // Must specify an address and city, state, or zip
          // appLogger.logError({
          //   message: err.message,
          //   context: 'PostingSearchModule#useLocationRestriction',
          // }); // logger is not supported on ZMC yet

          // eslint-disable-next-line
          console.error(`ERROR: ${err.message} :: Context: PostingSearchModule#useLocationRestriction`);
        }
      }
    });

    if (getDetailsRes && getDetailsRes.propertyTypeCode) {
      updatePropertyTypeFromApi(PROPERTY_TYPES_BY_CODE[getDetailsRes.propertyTypeCode]);
      newErrorObj.propertyType = '';
    }

    updateErrorObj(newErrorObj);
    updateIsLoading(false);

    const hasNoErrors = Object.values(newErrorObj).every((key) => !key);
    return hasNoErrors;
  }

  return { checkLocationRestriction, propertyTypeFromApi };
};

export const useValidateForm = (
  updateErrorObj: Dispatch<
    SetStateAction<{
      address?: string | JSX.Element;
      propertyType?: string | JSX.Element;
      numberOfUnits?: string;
    }>
  >,
) => {
  async function verifyAddress({ address, unit, errorObj }: { address: string; unit: string; errorObj: object }) {
    try {
      const { isValidatedWithoutUnit } = (await AddressActions.verify({ address, unit })()) as unknown as {
        isValidatedWithoutUnit: boolean;
      };

      // if the property is valid without a unit number
      if (unit !== '' && isValidatedWithoutUnit) {
        updateErrorObj({
          ...errorObj,
          address: `We are unable to validate the
                    property address with this unit number. Please double-check before creating the property.`,
        });
      }

      return true;
    } catch (err) {
      if (err.message) {
        updateErrorObj({
          ...errorObj,
          address: err.message,
        });
      }

      return false;
    }
  }

  const verifyNumUnits = ({ numUnits, errorObj }: { numUnits: string; errorObj: object }) => {
    if (isNaN(Number(numUnits))) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: 'Please enter a numeric value',
      });
    } else if (String(numUnits).includes('.')) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: 'Please enter a whole number',
      });
    } else if (Number(numUnits) > ValueConstants.MAX_NUM_UNITS_VALUE) {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: ValueConstants.MAX_NUM_UNITS_ERROR,
      });
    } else {
      updateErrorObj({
        ...errorObj,
        numberOfUnits: '',
      });
    }
  };

  const checkIsFormComplete = ({
    address,
    propertyType,
    numUnits,
    errorObj,
  }: {
    address: string;
    propertyType: string | null;
    numUnits: number | string;
    errorObj: {
      address?: string | JSX.Element;
      propertyType?: string | JSX.Element;
      numberOfUnits?: string;
    };
  }) => {
    const newErrorObj = {
      ...errorObj,
      address: address ? errorObj.address : 'Enter an address',
      propertyType: propertyType ? errorObj.propertyType : 'Enter a property type',
      numberOfUnits: !numUnits && propertyType === COMMUNITY ? 'Enter number of units' : errorObj.numberOfUnits,
    };

    updateErrorObj(newErrorObj);

    return Object.values(newErrorObj).every((key) => key === '');
  };

  return { checkIsFormComplete, verifyAddress, verifyNumUnits };
};
